import React from 'react';
import { AIAnalysisStepData, CapturedPhoto } from '../../types/submission-workflow';

interface AIAnalysisStepProps {
  photos: CapturedPhoto[];
  analysisData?: AIAnalysisStepData;
  onStartAnalysis: () => void;
  onRetryAnalysis: () => void;
  onSkipAnalysis: () => void;
  isLoading?: boolean;
}

export const AIAnalysisStep: React.FC<AIAnalysisStepProps> = ({
  photos,
  analysisData,
  onStartAnalysis,
  onRetryAnalysis,
  onSkipAnalysis,
  isLoading = false
}) => {
  const getStatusIcon = (status: AIAnalysisStepData['status']) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'processing': return '🔄';
      case 'completed': return '✅';
      case 'failed': return '❌';
      default: return '⏳';
    }
  };

  const getStatusText = (status: AIAnalysisStepData['status']) => {
    switch (status) {
      case 'pending': return 'Ready to analyze';
      case 'processing': return 'Analyzing images...';
      case 'completed': return 'Analysis complete';
      case 'failed': return 'Analysis failed';
      default: return 'Ready to analyze';
    }
  };

  const getStatusColor = (status: AIAnalysisStepData['status']) => {
    switch (status) {
      case 'pending': return 'text-urban-300';
      case 'processing': return 'text-treasure';
      case 'completed': return 'text-verge';
      case 'failed': return 'text-rust';
      default: return 'text-urban-300';
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-urban mb-2">AI Analysis</h2>
        <p className="text-urban-300">
          Our AI will analyze your photos to suggest tags and categories
        </p>
      </div>

      {/* Analysis Status */}
      <div className="bg-white rounded-lg border border-urban-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{getStatusIcon(analysisData?.status || 'pending')}</span>
            <div>
              <h3 className="font-medium text-urban">Analysis Status</h3>
              <p className={`text-sm ${getStatusColor(analysisData?.status || 'pending')}`}>
                {getStatusText(analysisData?.status || 'pending')}
              </p>
            </div>
          </div>
          
          {analysisData?.processingTimeMs && (
            <div className="text-right">
              <p className="text-sm text-urban-300">Processing Time</p>
              <p className="font-medium text-urban">{analysisData.processingTimeMs}ms</p>
            </div>
          )}
        </div>

        {/* Processing Animation */}
        {(analysisData?.status === 'processing' || isLoading) && (
          <div className="space-y-4">
            <div className="w-full bg-teddy-200 rounded-full h-2">
              <div className="bg-treasure h-2 rounded-full animate-pulse" style={{ width: '60%' }} />
            </div>
            <p className="text-sm text-urban-300 text-center">
              Analyzing {photos.length} photo{photos.length !== 1 ? 's' : ''}...
            </p>
            {analysisData?.error && analysisData.error.includes('retrying') && (
              <p className="text-xs text-treasure-600 text-center">
                {analysisData.error}
              </p>
            )}
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 text-sm text-urban-300">
                <div className="animate-spin w-4 h-4 border-2 border-treasure border-t-transparent rounded-full"></div>
                <span>This may take a few moments...</span>
              </div>
            </div>
          </div>
        )}

        {/* AI Status Message (Mock/Failed/Unavailable) */}
        {analysisData?.statusMessage && analysisData.aiStatus !== 'success' && (
          <div className="bg-treasure-50 border border-treasure-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">🧙‍♂️</span>
              <div className="flex-1">
                <h4 className="font-medium text-treasure-800">AI Analysis Status</h4>
                <p className="text-treasure-700 mt-1">
                  {analysisData.statusMessage}
                </p>
                {analysisData.aiStatus === 'mock' && (
                  <p className="text-sm text-treasure-600 mt-2">
                    💡 The analysis results below are for demonstration purposes. You can still use them as suggestions!
                  </p>
                )}
                {(analysisData.aiStatus === 'failed' || analysisData.aiStatus === 'unavailable') && (
                  <p className="text-sm text-treasure-600 mt-2">
                    💡 You can continue to the next step and add tags manually.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {analysisData?.status === 'failed' && (
          <div className="bg-rust-50 border border-rust-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="text-rust text-xl">⚠️</span>
              <div className="flex-1">
                <h4 className="font-medium text-rust-800">Analysis Failed</h4>
                <p className="text-rust-700 mt-1">
                  {analysisData.error || 'An error occurred during analysis. Please try again.'}
                </p>
                <div className="mt-3 space-x-3">
                  <button
                    onClick={onRetryAnalysis}
                    className="bg-rust text-white px-4 py-2 rounded-md hover:bg-rust-600 transition-colors"
                  >
                    Retry Analysis
                  </button>
                  {analysisData.error?.includes('unavailable') && (
                    <button
                      onClick={onSkipAnalysis}
                      className="bg-urban-300 text-white px-4 py-2 rounded-md hover:bg-urban-400 transition-colors"
                    >
                      Continue Without AI
                    </button>
                  )}
                </div>
                {analysisData.error?.includes('unavailable') && (
                  <p className="text-sm text-rust-600 mt-2">
                    💡 You can continue without AI suggestions and add tags manually in the next step.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Start Analysis Button */}
        {!analysisData && !isLoading && (
          <div className="text-center">
            {(() => {
              const unuploadedPhotos = photos.filter(photo => !photo.isUploaded);
              const hasUnuploaded = unuploadedPhotos.length > 0;
              
              return (
                <>
                  <button
                    onClick={onStartAnalysis}
                    disabled={photos.length === 0 || hasUnuploaded}
                    className="bg-cardboard text-white px-6 py-3 rounded-lg font-medium hover:bg-cardboard-600 transition-colors disabled:bg-urban-200 disabled:cursor-not-allowed"
                  >
                    Start AI Analysis
                  </button>
                  {photos.length === 0 && (
                    <p className="text-sm text-urban-300 mt-2">
                      Please add photos before starting analysis
                    </p>
                  )}
                  {hasUnuploaded && (
                    <p className="text-sm text-treasure-600 mt-2">
                      ⏳ Waiting for {unuploadedPhotos.length} photo(s) to finish uploading...
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Photo Grid with Analysis Results */}
      <div className="space-y-4">
        <h3 className="font-medium text-urban">Photos to Analyze ({photos.length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {photos.map((photo) => {
            const result = analysisData?.results.find(r => r.imageId === photo.id);
            const isRejected = analysisData?.rejectedImages.find(r => r.imageId === photo.id);
            
            return (
              <div key={photo.id} className="bg-white rounded-lg border border-urban-200 overflow-hidden">
                <div className="relative">
                  <img
                    src={photo.url}
                    alt={`Photo ${photo.id}`}
                    className="w-full h-48 object-cover"
                  />
                  
                  {/* Upload Status Badge */}
                  {!photo.isUploaded && (
                    <div className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium bg-treasure text-white">
                      Uploading...
                    </div>
                  )}
                  
                  {/* Analysis Status Badge */}
                  {photo.isUploaded && analysisData?.status === 'completed' && (
                    <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                      isRejected 
                        ? 'bg-rust text-white' 
                        : result?.isCompliant 
                          ? 'bg-verge text-white' 
                          : 'bg-treasure text-urban'
                    }`}>
                      {isRejected ? 'Rejected' : result?.isCompliant ? 'Approved' : 'Pending'}
                    </div>
                  )}
                  
                  {/* Processing Overlay */}
                  {analysisData?.status === 'processing' && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="animate-spin text-2xl mb-2">🔄</div>
                        <p className="text-sm">Analyzing...</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Analysis Results */}
                <div className="p-4">
                  {isRejected ? (
                    <div className="space-y-2">
                      <h4 className="font-medium text-rust">Image Rejected</h4>
                      <p className="text-sm text-rust-700">{isRejected.reason}</p>
                    </div>
                  ) : result ? (
                    <div className="space-y-3">
                      {/* Confidence Score */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-urban">Confidence</span>
                        <span className="text-sm text-urban-300">
                          {Math.round(result.confidence * 100)}%
                        </span>
                      </div>

                      {/* Suggested Tags */}
                      {result.suggestedTags.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-urban mb-2">Suggested Tags</h5>
                          <div className="flex flex-wrap gap-1">
                            {result.suggestedTags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-verge text-white text-xs rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Suggested Categories */}
                      {result.suggestedCategories.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-urban mb-2">Suggested Categories</h5>
                          <div className="flex flex-wrap gap-1">
                            {result.suggestedCategories.map((category, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-cardboard text-white text-xs rounded-full"
                              >
                                {category}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-urban-300">
                      <p className="text-sm">Waiting for analysis...</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* User Message (Friendly Error) */}
      {analysisData?.userMessage && (
        <div className="bg-treasure-50 border border-treasure-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <span className="text-2xl">🦜</span>
            <div>
              <h4 className="font-medium text-treasure-800 mb-1">AI Analysis Update</h4>
              <p className="text-treasure-700">{analysisData.userMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Combined Suggestions Summary */}
      {analysisData?.status === 'completed' && (
        <div className="bg-verge-50 border border-verge-200 rounded-lg p-6">
          <h3 className="font-medium text-verge-800 mb-4">📋 Analysis Summary</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Combined Tags */}
            <div>
              <h4 className="font-medium text-verge-700 mb-2">
                All Suggested Tags ({analysisData.combinedSuggestions.tags.length})
              </h4>
              {analysisData.combinedSuggestions.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {analysisData.combinedSuggestions.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-verge text-white text-sm rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-verge-600">No tags suggested</p>
              )}
            </div>

            {/* Combined Categories */}
            <div>
              <h4 className="font-medium text-verge-700 mb-2">
                All Suggested Categories ({analysisData.combinedSuggestions.categories.length})
              </h4>
              {analysisData.combinedSuggestions.categories.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {analysisData.combinedSuggestions.categories.map((category, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-cardboard text-white text-sm rounded-full"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-verge-600">No categories suggested</p>
              )}
            </div>
          </div>

          {/* Rejected Images Summary */}
          {analysisData.rejectedImages.length > 0 && (
            <div className="mt-4 pt-4 border-t border-verge-200">
              <h4 className="font-medium text-rust-700 mb-2">
                ⚠️ Rejected Images ({analysisData.rejectedImages.length})
              </h4>
              <div className="space-y-1">
                {analysisData.rejectedImages.map((rejected, index) => (
                  <p key={index} className="text-sm text-rust-600">
                    • {rejected.originalFilename}: {rejected.reason}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="bg-treasure-50 border border-treasure-200 rounded-lg p-4">
        <h4 className="font-medium text-treasure-800 mb-2">🤖 About AI Analysis</h4>
        <ul className="text-sm text-treasure-700 space-y-1">
          <li>• AI analyzes your photos to identify objects and suggest relevant tags</li>
          <li>• Categories help organize items (e.g., furniture, electronics, books)</li>
          <li>• You can edit all suggestions in the next step</li>
          <li>• Images may be rejected if they don't meet platform guidelines</li>
        </ul>
      </div>
    </div>
  );
};